import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { avanzoApi, PAES_STAGE_LABELS } from "@/lib/avanzoApi";
import PaesImage from "@/components/PaesImage";

/**
 * /avanzo/entrenar-v2 — Buddy AVANZA · TRAIN v2 (PAES 2026, Fase 5).
 *
 * Sesión determinística de 13 reactivos con 4 etapas:
 *   Activación (2) → Con Buddy (4) → Práctica (4) → Reto AVANZO (3)
 * El cierre no consume un ítem 14. La sesión termina al completar los 13,
 * independientemente del tiempo. Duración estimada (~15 min) es referencia.
 *
 * Endpoints consumidos:
 *   POST /avanzo-b2c/paes/session/start
 *   POST /avanzo-b2c/paes/session/{id}/next
 *   POST /avanzo-b2c/paes/session/{id}/answer
 *   POST /avanzo-b2c/paes/tutor/help    (nivel 1..3)
 */
export default function AvanzoTrainV2({ practiceMode = false, practiceParams = null }) {
  const [phase, setPhase] = useState("loading");           // loading|answering|feedback|closing|error
  const [sessionId, setSessionId] = useState(null);
  const [stagePlan, setStagePlan] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [stage, setStage] = useState(null);
  const [item, setItem] = useState(null);
  const [choice, setChoice] = useState(null);
  const [helpsUsed, setHelpsUsed] = useState(0);
  const [tutorText, setTutorText] = useState(null);
  const [tutorLevel, setTutorLevel] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [closing, setClosing] = useState(null);
  const [error, setError] = useState(null);
  const startTsRef = useRef(null);
  const submittingRef = useRef(false);
  const nextLoadingRef = useRef(false);
  const bootKeyRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [nextLoading, setNextLoading] = useState(false);
  const [inlineError, setInlineError] = useState(null);

  const bootstrap = useCallback(async () => {
    try {
      setPhase("loading");
      const body = practiceMode
        ? { mode: "practice", subject: practiceParams?.subject, habilidad: practiceParams?.habilidad, item_count: practiceParams?.item_count || 5 }
        : {};
      const r = await avanzoApi.paesSessionStart({ ...body, resume: true });
      setSessionId(r.data.session_id);
      setStagePlan(r.data.stage_plan || []);
      await loadNext(r.data.session_id);
    } catch (e) {
      setError(e?.response?.data?.detail || "No se pudo iniciar la sesión.");
      setPhase("error");
    }
  }, [practiceMode, practiceParams]);

  const loadNext = useCallback(async (sid) => {
    if (nextLoadingRef.current) return;
    nextLoadingRef.current = true;
    setNextLoading(true);
    try {
      const r = await avanzoApi.paesSessionNext(sid);
      setInlineError(null);
      if (r.data.item) {
        setItem(r.data.item);
        setStage(r.data.stage);
        setCurrentIdx(r.data.session_index);
        setChoice(null);
        setHelpsUsed(0);
        setTutorText(null);
        setTutorLevel(0);
        setFeedback(null);
        startTsRef.current = Date.now();
        setPhase("answering");
      } else {
        setClosing(r.data.closing || { items_completed: currentIdx });
        setPhase("closing");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "No se pudo cargar el siguiente reactivo.");
      setPhase("error");
    } finally {
      nextLoadingRef.current = false;
      setNextLoading(false);
    }
  }, [currentIdx]);

  const bootKey = JSON.stringify({ practiceMode, practiceParams });
  useEffect(() => {
    if (bootKeyRef.current === bootKey) return;
    bootKeyRef.current = bootKey;
    bootstrap();
  }, [bootKey, bootstrap]);

  const askHint = useCallback(async () => {
    if (!item || tutorLevel >= 3) return;
    const nextLevel = Math.min(3, tutorLevel + 1);
    try {
      const r = await avanzoApi.paesTutorHelp({
        item_id: item.item_id,
        level: nextLevel,
        student_letter: choice || null,
        stage,
      });
      setTutorText(r.data.text || null);
      setTutorLevel(nextLevel);
      setHelpsUsed((h) => h + 1);
    } catch (e) {
      setTutorText("No pudimos cargar la pista en este momento.");
    }
  }, [item, tutorLevel, choice, stage]);

  const submit = useCallback(async () => {
    if (!choice || !item || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setInlineError(null);
    const rt = startTsRef.current ? (Date.now() - startTsRef.current) / 1000 : null;
    try {
      const r = await avanzoApi.paesSessionAnswer(sessionId, {
        item_id: item.item_id,
        student_letter: choice,
        stage,
        helps_used: helpsUsed,
        response_time_seconds: rt,
      });
      if (r.data.idempotent_replay && r.data.student_letter) setChoice(r.data.student_letter);
      setFeedback(r.data);
      setPhase("feedback");
    } catch (e) {
      const status = e?.response?.status;
      if (!status || status >= 500) {
        setInlineError("No se pudo enviar. Revisa tu conexión e inténtalo de nuevo.");
      } else {
        await loadNext(sessionId);
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [choice, item, sessionId, stage, helpsUsed, loadNext]);

  // Progress bar with 4 stage groups
  const stageGroups = useMemo(() => {
    const groups = { activation: [], guided: [], variable_practice: [], avanzo_challenge: [] };
    stagePlan.forEach((s, i) => {
      if (groups[s]) groups[s].push({ idx: i, done: i < currentIdx });
    });
    return groups;
  }, [stagePlan, currentIdx]);

  if (phase === "loading") {
    return (
      <div className="p-8 text-center" data-testid="paes-train-loading">
        <div className="text-slate-500">Preparando tu sesión…</div>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="paes-train-error">
        <h2 className="text-xl font-semibold text-red-700 mb-3">No pudimos iniciar</h2>
        <p className="text-slate-600 mb-4">{error}</p>
        <button onClick={() => (sessionId ? loadNext(sessionId) : bootstrap())}
                className="px-4 py-2 bg-slate-900 text-white rounded"
                data-testid="paes-train-retry-btn">Reintentar</button>
      </div>
    );
  }

  if (phase === "closing" && closing) {
    return (
      <div className="p-6 max-w-2xl mx-auto" data-testid="paes-train-closing">
        <h1 className="text-2xl font-semibold mb-2">Sesión completa</h1>
        <p className="text-slate-600 mb-6">
          Completaste {closing.items_completed || 13} reactivos.
          {closing.materias_cubiertas?.length > 0 && (
            <> Cubriste: {closing.materias_cubiertas.join(", ")}.</>
          )}
        </p>
        {closing.fortaleza?.length > 0 && (
          <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded">
            <div className="font-medium text-emerald-800">Fortaleza detectada</div>
            <div className="text-sm text-emerald-700">{closing.fortaleza.join(", ")}</div>
          </div>
        )}
        {closing.reforzar?.length > 0 && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded">
            <div className="font-medium text-amber-800">A reforzar</div>
            <div className="text-sm text-amber-700">{closing.reforzar.join(", ")}</div>
          </div>
        )}
        <div className="flex gap-3">
          <button onClick={bootstrap} className="px-4 py-2 bg-slate-900 text-white rounded"
                  data-testid="paes-train-restart-btn">Otra sesión</button>
          <Link to="/avanzo/progreso" className="px-4 py-2 border border-slate-300 rounded"
                data-testid="paes-train-go-progress">Ver mi progreso</Link>
        </div>
      </div>
    );
  }

  // answering / feedback phases
  return (
    <div className="p-4 max-w-3xl mx-auto" data-testid="paes-train-active">
      {/* Progress bar */}
      <div className="mb-6" data-testid="paes-train-progress">
        <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
          <span data-testid="paes-train-stage-label">{PAES_STAGE_LABELS[stage] || stage}</span>
          <span className="opacity-50">·</span>
          <span>Reactivo {currentIdx + 1} de {stagePlan.length}</span>
        </div>
        <div className="flex gap-1">
          {Object.entries(stageGroups).map(([sname, slots]) =>
            slots.length > 0 ? (
              <div key={sname} className="flex gap-0.5 flex-1"
                   data-testid={`paes-stage-group-${sname}`}>
                {slots.map((s) => (
                  <div key={s.idx}
                       className={`h-2 flex-1 rounded ${
                         s.idx === currentIdx ? "bg-slate-900" :
                         s.done ? "bg-emerald-500" : "bg-slate-200"
                       }`}
                       data-testid={`paes-slot-${s.idx}`} />
                ))}
              </div>
            ) : null
          )}
        </div>
      </div>

      {/* Item card */}
      {item && (
        <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="paes-item-card">
          <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">
            {item.materia} · {item.habilidad}
          </div>
          {item.contexto_estimulo && (
            <div className="mb-3 text-slate-700 whitespace-pre-wrap"
                 data-testid="paes-item-context">
              {item.contexto_estimulo}
            </div>
          )}
          {/* Image renderer (no crop, zoomable) */}
          <PaesImage item={item} />
          <h2 className="text-lg font-medium mb-4 mt-3" data-testid="paes-item-question">
            {item.pregunta}
          </h2>

          <div className="grid gap-2">
            {["A", "B", "C", "D"].map((L) => (
              <button
                key={L}
                type="button"
                disabled={phase !== "answering" || submitting}
                onClick={() => setChoice(L)}
                className={`text-left p-3 rounded border transition ${
                  choice === L
                    ? "border-slate-900 bg-slate-50"
                    : "border-slate-200 hover:border-slate-400"
                }`}
                data-testid={`paes-option-${L}`}
              >
                <span className="font-medium mr-2">{L}.</span>
                {item.opciones?.[L] || "—"}
              </button>
            ))}
          </div>

          {phase === "answering" && (
            <>
              {tutorText && (
                <div className="mt-4 p-3 bg-sky-50 border border-sky-200 rounded"
                     data-testid="paes-tutor-text">
                  <div className="text-xs font-medium text-sky-700 mb-1">Buddy</div>
                  <div className="text-sm text-sky-900">{tutorText}</div>
                </div>
              )}
              <div className="mt-5 flex items-center gap-3">
                {stage !== "avanzo_challenge" && tutorLevel < 2 && (
                  <button onClick={askHint}
                          className="px-3 py-2 text-sm border border-slate-300 rounded"
                          data-testid="paes-hint-btn">
                    {tutorLevel === 0 ? "Necesito una pista" : "Necesito otra ayuda"}
                  </button>
                )}
                <button
                  onClick={submit}
                  disabled={!choice || submitting}
                  className="ml-auto px-5 py-2 bg-slate-900 text-white rounded disabled:opacity-40"
                  data-testid="paes-submit-btn"
                >
                  Enviar respuesta
                </button>
              </div>
              {inlineError && (
                <p className="text-sm text-red-700 mt-2" data-testid="paes-inline-error">
                  {inlineError}
                </p>
              )}
            </>
          )}

          {phase === "feedback" && feedback && (
            <div
              className={`mt-5 p-4 rounded border ${
                feedback.correct
                  ? "bg-emerald-50 border-emerald-200"
                  : "bg-amber-50 border-amber-200"
              }`}
              data-testid="paes-feedback"
            >
              <div className={`font-medium mb-1 ${
                feedback.correct ? "text-emerald-800" : "text-amber-800"
              }`}>
                {feedback.correct ? "Correcto" : "Revisémoslo"}
              </div>
              <div className="text-sm text-slate-800" data-testid="paes-feedback-text">
                {feedback.feedback_text}
              </div>
              {feedback.correct_letter && (
                <div className="text-xs text-slate-600 mt-2">
                  Respuesta correcta: {feedback.correct_letter}
                </div>
              )}
              <div className="mt-4 flex gap-3">
                <button
                  onClick={() => loadNext(sessionId)}
                  disabled={nextLoading}
                  className="px-4 py-2 bg-slate-900 text-white rounded disabled:opacity-40"
                  data-testid="paes-next-btn"
                >
                  {feedback.next_action === "transfer_same_skill"
                    ? "Probar otra"
                    : currentIdx + 1 >= stagePlan.length
                    ? "Ver cierre"
                    : "Siguiente"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
